import { select, call, put } from 'redux-saga/effects';
import {
  getExerciseFilters,
  getExercises,
  getExercisesAutoComplete,
  toggleFavorite as toggleFavoriteApi,
} from 'services/api/builder';
import { createCustomExercise as createCustomExerciseApi } from 'services/api/program';
import { filters as filterKeys } from 'constants/exercisesFilters';

import {
  ExercisesSelectors as Selectors,
  ExercisesActions as Actions,
  ExercisesTypes as Types,
} from './exercisesReducer';

let searchController;

// the API returns both kinds unless asked for one of them, so the
// parameter is only sent when a single kind is selected
const contentTypeFromFilters = (filters) => {
  const kinds = [
    filters[filterKeys.exercisesContent] && 'exercises',
    filters[filterKeys.educationContent] && 'lessons',
  ].filter(Boolean);

  return kinds.length === 1 ? kinds[0] : undefined;
};

// runs when search is triggered
function* fetchExercises() {
  if (searchController) {
    searchController.abort();
  }

  const navigation = yield select(Selectors.getNavigation);
  const search = yield select(Selectors.getSearch);
  const filters = yield select(Selectors.getFilters);
  const activeFilters = yield select(Selectors.getActiveFilters);

  yield put(Actions.setExercisesLoading(true));

  const query = {
    search: search.trim(),
    page: navigation.currentPage,
    limit: navigation.limit,
    favorites: filters[filterKeys.favoritesOnly],
    filters: activeFilters,
    alias: filters[filterKeys.aliasOnly],
    shared: filters[filterKeys.sharedExercises],
    contentType: contentTypeFromFilters(filters),
  };

  const cacheKey = JSON.stringify(query);
  const cache = yield select(Selectors.getCache);
  const cachedEntry = cache[cacheKey];
  const now = Date.now();

  if (cachedEntry && now - cachedEntry.fetchedAt < 30000 && !query.alias) {
    const { count, rows, userId } = cachedEntry.data;
    const totalPages = Math.ceil(count / navigation.limit);

    yield put(Actions.setNavigation({ totalPages }));

    const response = rows.map((r) => ({
      ...r,
      userId,
    }));

    yield put(Actions.fetchExercisesSuccess(response));
    yield put(Actions.setExercisesLoading(false));
    return;
  }

  try {
    searchController = new AbortController();
    const { rows, count, userId } = yield call(getExercises, {
      ...query,
      signal: searchController.signal,
    });

    yield put(
      Actions.updateCache(cacheKey, {
        fetchedAt: Date.now(),
        data: { count, rows, userId },
      })
    );

    const totalPages = Math.ceil(count / navigation.limit);

    yield put(Actions.setNavigation({ totalPages }));

    const response = rows.map((r) => ({
      ...r,
      userId,
    }));

    yield put(Actions.fetchExercisesSuccess(response));
  } catch (error) {
    // the API answers 429 when search is still throttled after its retries
    if (error.response?.status === 429) {
      yield put(Actions.fetchExercisesUnavailable());
    } else {
      yield put(
        Actions.setExercisesErrors([`[fetchExcercises error]: ${error}`])
      );
    }
  } finally {
    yield put(Actions.setExercisesLoading(false));
  }
}

let autocompleteController;

// runs to get auto complete for text box
function* fetchAutoComplete() {
  if (autocompleteController) {
    autocompleteController.abort();
  }

  const search = yield select(Selectors.getSearchText);

  if (!search || search.length <= 1) {
    yield put(Actions.fetchAutoCompleteSuccess([]));
    return;
  }

  try {
    const filters = yield select(Selectors.getFilters);
    autocompleteController = new AbortController();
    yield put(Actions.setAutoCompleteLoading(true));
    const { rows } = yield call(getExercisesAutoComplete, {
      search,
      signal: autocompleteController.signal,
      contentType: contentTypeFromFilters(filters),
    });

    const data = rows.map((r) => ({ ...r, label: r.name }));

    yield put(Actions.fetchAutoCompleteSuccess(data));
  } catch (error) {
    // a throttled search offers no suggestions and keeps the results on screen
    if (error.response?.status === 429) {
      yield put(Actions.fetchAutoCompleteSuccess([]));
    } else {
      yield put(
        Actions.setAutoCompleteErrors([`[fetchExcercises error]: ${error}`])
      );
    }
  } finally {
    yield put(Actions.setAutoCompleteLoading(false));
  }
}

function* fetchExercisesFilters() {
  yield put(Actions.setExercisesFiltersLoading(true));
  try {
    const results = yield call(getExerciseFilters);
    yield put(Actions.fetchExercisesFiltersSuccess(results));
  } catch (error) {
    yield put(
      Actions.setExercisesFiltersErrors([
        `[fetchExcercisesFilters error]: ${error}`,
      ])
    );
  } finally {
    yield put(Actions.setExercisesFiltersLoading(false));
  }
}

function* toggleFavorite({ groupId, favorited }) {
  try {
    yield put(Actions.invalidateCache());
    yield call(toggleFavoriteApi, groupId, favorited);
  } catch (error) {
    console.error('Failed to toggle favorite', error);
  }
}

function* createCustomExercise({ data, callbacks }) {
  try {
    yield call(createCustomExerciseApi, data);
    yield put(Actions.invalidateCache());
    yield put(Actions.fetchExercises());
    if (callbacks?.onSuccess) callbacks.onSuccess();
  } catch (error) {
    if (callbacks?.onError) callbacks.onError(error);
  }
}

export default () => ({
  [Types.FETCH_EXERCISES]: fetchExercises,
  [Types.FETCH_AUTO_COMPLETE]: fetchAutoComplete,
  [Types.FETCH_EXERCISES_FILTERS]: fetchExercisesFilters,
  [Types.TOGGLE_FAVORITE]: toggleFavorite,
  [Types.CREATE_CUSTOM_EXERCISE]: createCustomExercise,
});
